import { Page, pickSeoProps } from '@components/Page'
import { RolexBreadcrumbs } from '@components/_rolex/Breadcrumbs'
import { RolexImage } from '@components/_rolex/Image'
import RolexPage from '@components/_rolex/Page'
import { RolexRichText } from '@components/_rolex/RichText'
import { RolexContactFormFields } from '@components/_rolex/_contact-form/Fields'
import { RolexContactFormForm } from '@components/_rolex/_contact-form/Form'
import { Container } from '@components/gassan-ui'
import { Heading } from '@components/rolex-ui/Heading'
import { SecondaryCta } from '@components/rolex-ui/SecondaryCta'
import { Text } from '@components/rolex-ui/Text'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { i18nTranslationNamespaces } from '@lib/i18n-translation-files'
import { SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { RolexSendUsAMessage } from '@lib/kontent/models/RolexSendUsAMessage'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { useState } from 'react'

export type SendAMessageDisplayMode = 'message' | 'other-fields' | 'success'
type SendAMessagePageProps = SingleItemResponse<RolexSendUsAMessage>

const SendAMessagePage: NextPage<SendAMessagePageProps> = ({ item }) => {
  const { locales, locale } = useRouter()
  const { t } = useTranslation('rolex')
  const [displayMode, setDisplayMode] = useState<SendAMessageDisplayMode>('message')

  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateNavigation(locales, 'rolex.sendAMessage', '/rolex')}
      noindex={locale === 'de'}
    >
      <RolexPage pageType="contact form page">
        <RolexBreadcrumbs
          items={[
            {
              label: t('navigation.contactUs', { ns: 'rolex' }),
              href: `/rolex/contact`,
            },
          ]}
          currentPageTitle={t('contactUs', { ns: 'rolex' })}
        />
        <RolexImage
          size="xl"
          mobileImage={item.elements.mobile_image}
          desktopImage={item.elements.desktop_image}
          className="!mb-0"
        />
        <div className="bg-rolexLightBeige">
          <Container className="relative grid grid-cols-6 gap-2 py-[60px] md:grid-cols-12 md:py-[90px]">
            {displayMode === 'other-fields' && (
              <SecondaryCta
                iconLeft="chevron-left"
                className="absolute left-0 top-8"
                as="button"
                onClick={() => setDisplayMode('message')}
              >
                Back
              </SecondaryCta>
            )}
            <div className="col-span-6  md:col-span-8 md:col-start-3 lg:col-span-6 lg:col-start-4">
              <div className="flex flex-col text-center">
                <Text variant="body24" className="mb-2 text-rolexBrown">
                  {item.elements.pretitle.value}
                </Text>
                <Heading variant="headline50" className="mb-8 text-rolexBrown">
                  {displayMode === 'message' && item.elements.send_a_message_title.value}
                  {displayMode === 'other-fields' &&
                    item.elements.enter_contact_details_title.value}
                  {displayMode === 'success' && t('thanks', { ns: 'other' })}
                </Heading>
                <Text
                  variant="fixed16"
                  weight="light"
                  className="mb-4 w-full max-w-[500px] self-center"
                >
                  {displayMode === 'message' && item.elements.send_a_message_description.value}
                  {displayMode === 'other-fields' &&
                    item.elements.enter_contact_details_description.value}
                </Text>
                {displayMode === 'success' && (
                  <RolexRichText content={item.elements.success_description} />
                )}
              </div>
              <RolexContactFormForm
                renderFields={
                  <RolexContactFormFields
                    displayMode={displayMode}
                    setDisplayMode={setDisplayMode}
                  />
                }
                handleSuccessfullSubmit={() => setDisplayMode('success')}
              />
            </div>
          </Container>
        </div>
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps<SendAMessagePageProps> = async ({
  locale,
  preview,
}) => {
  try {
    const language = ['sg', 'de'].includes(locale) ? 'en' : locale
    const content = await getPageByCodename<RolexSendUsAMessage>({
      codename: 'rolex___send_us_a_message',
      lang: language,
      preview,
    })

    return {
      props: {
        ...content,
        ...(await serverSideTranslations(language, i18nTranslationNamespaces)),
      },
    }
  } catch (error) {
    throw error
  }
}

export default SendAMessagePage
